/**
 * Keyboard Shortcuts Dialog
 * Displays all available keyboard shortcuts
 * 
 * Location: src/components/cat/KeyboardShortcutsDialog.tsx
 */

import React from 'react'
import { X, Keyboard } from 'lucide-react'
import Button from '@/components/common/Button'

interface KeyboardShortcutsDialogProps {
  isOpen: boolean
  onClose: () => void
}

interface Shortcut {
  keys: string[]
  description: string
  category: string
}

const KeyboardShortcutsDialog: React.FC<KeyboardShortcutsDialogProps> = ({
  isOpen,
  onClose
}) => {
  if (!isOpen) return null

  const shortcuts: Shortcut[] = [
    // Navigation
    { keys: ['Ctrl', '↑'], description: 'Previous segment', category: 'Navigation' },
    { keys: ['Ctrl', '↓'], description: 'Next segment', category: 'Navigation' },
    { keys: ['Ctrl', 'Home'], description: 'First segment', category: 'Navigation' },
    { keys: ['Ctrl', 'End'], description: 'Last segment', category: 'Navigation' },
    { keys: ['Ctrl', 'G'], description: 'Go to segment', category: 'Navigation' },

    // Editing
    { keys: ['Ctrl', 'Shift', 'S'], description: 'Copy source to target', category: 'Editing' },
    { keys: ['Ctrl', 'Enter'], description: 'Confirm and next', category: 'Editing' },
    { keys: ['Ctrl', 'S'], description: 'Save all changes', category: 'Editing' },

    // Tools
    { keys: ['Ctrl', 'M'], description: 'Toggle TM panel', category: 'Tools' },
    { keys: ['Ctrl', 'T'], description: 'Toggle MT panel', category: 'Tools' },
    { keys: ['Ctrl', 'Q'], description: 'Run QA check', category: 'Tools' },
    { keys: ['F8'], description: 'Next QA issue', category: 'Tools' },
    { keys: ['Shift', 'F8'], description: 'Previous QA issue', category: 'Tools' },

    // General
    { keys: ['F1'], description: 'Show this help', category: 'General' },
    { keys: ['Esc'], description: 'Close dialogs', category: 'General' },
  ]

  const categories = Array.from(new Set(shortcuts.map(s => s.category)))

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose()
    }
  }

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onKeyDown={handleKeyDown}
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Keyboard className="h-6 w-6 text-gold-600" />
            <h3 className="text-xl font-semibold text-gray-900">
              Keyboard Shortcuts
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {categories.map(category => (
              <div key={category}>
                <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3">
                  {category}
                </h4>
                <div className="space-y-2">
                  {shortcuts
                    .filter(s => s.category === category)
                    .map((shortcut, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between py-2 px-3 hover:bg-gray-50 rounded-lg"
                      >
                        <span className="text-gray-700">{shortcut.description}</span>
                        <div className="flex items-center space-x-1">
                          {shortcut.keys.map((key, keyIndex) => (
                            <React.Fragment key={keyIndex}>
                              <kbd className="px-3 py-1.5 bg-gray-100 border border-gray-300 rounded text-sm font-medium text-gray-900 shadow-sm">
                                {key}
                              </kbd>
                              {keyIndex < shortcut.keys.length - 1 && (
                                <span className="text-gray-400">+</span>
                              )}
                            </React.Fragment>
                          ))}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">
              💡 <strong>Tip:</strong> Press <kbd className="px-2 py-1 bg-gray-200 rounded text-xs">F1</kbd> anytime to show this help
            </p>
            <Button
              variant="primary"
              onClick={onClose}
            >
              Got it
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default KeyboardShortcutsDialog